import ActionButton from "@/components/buttons/ActionButton";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import BackNavigationArrow from "@/components/views/BackNavigationArrow";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import { phoneNumberNormalizeFunction, resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { App as AntApp, Button, Card, Checkbox, Col, Collapse, Divider, Form, Input, Row, Tree } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import {
  useAdminCreateUserWithAgentUserAccounts,
  useEnumeration,
  useGetAgentEnumerations,
  useGetPermissionDefinitions,
} from "../../../common/api/queries";
import { MOBILE_NUMBER_COUNTRY_WHITELIST, rowGutter } from "../../../common/constants";
import { Permission } from "../../../common/security/enums";
import { AntTreeStrictCheckedProps } from "../../../common/types";
import { UUID } from "../../../typings/global";
import { AgentType } from "../../agent/enums";
import { formatAgentName } from "../../agent/utils";
import { useIsSystemAdmin } from "../../auth/hooks";
import { AgentSelect } from "../../enumerations/components/form/AgentSelect";
import { EnumerationType } from "../../enumerations/enums";
import { DefaultPermissionsFormPart } from "../components/forms/DefaultPermissionsFormPart";
import { ADMIN_USER_ROUTE_PATHS } from "../paths";
import { AdminCreateUpdateAgentUserAccount, AdminCreateUserWithAgentUserAccounts } from "../types";
import { buildPermissionTreeNodes, filterPermissionsWithPrerequisites, isSystemAdminOnlyPermission } from "../utils";

interface UserAccountState {
  permissions: Permission[];
  agentId?: UUID;
  representingAgentId?: UUID;
}

const colSpan = 6;

export const AdminUserCreateContainer = () => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const navigate = useNavigate();

  const isCurrentUserSystemAdmin = useIsSystemAdmin();
  const topAgentAllowedFeatures = useEnumeration(EnumerationType.TOP_AGENT_ALLOWED_FEATURES);

  const { data: permissionDefinitions } = useGetPermissionDefinitions();

  const [form] = Form.useForm<AdminCreateUserWithAgentUserAccounts>();
  const isSystemAdmin = Form.useWatch("systemAdmin", form);

  const [userAccounts, setUserAccounts] = useState<UserAccountState[]>([{ permissions: [] }]);

  // One request covers the agents of all accounts, so the previous data keeps the other accounts named while it reloads
  const { data: agentData } = useGetAgentEnumerations(
    userAccounts.map((account) => account.agentId).filter((agentId) => agentId !== undefined),
    true,
  );

  const { data: representingAgentData } = useGetAgentEnumerations(
    userAccounts.map((account) => account.representingAgentId).filter((agentId) => agentId !== undefined),
    true,
  );

  const findAgent = (agentId?: UUID) => agentData?.find((agent) => agent.id === agentId);
  const findRepresentingAgent = (agentId?: UUID) => representingAgentData?.find((agent) => agent.id === agentId);

  const adminCreateUserWithAgentUserAccounts = useAdminCreateUserWithAgentUserAccounts();

  const handleAccountAdd = () => {
    const accounts = [...(form.getFieldValue("accounts") ?? [])];

    accounts.push({
      disabled: false,
      permissions: [],
      agentId: undefined,
      representingAgentId: undefined,
    });
    form.setFieldsValue({ accounts: accounts as AdminCreateUpdateAgentUserAccount[] });

    const changedUserAccounts = [...userAccounts];
    changedUserAccounts.push({ permissions: [] });
    setUserAccounts(changedUserAccounts);
  };

  const handleAccountDelete = (index: number) => {
    const accounts = [...form.getFieldValue("accounts")] as AdminCreateUpdateAgentUserAccount[];
    accounts.splice(index, 1);
    form.setFieldsValue({ accounts });

    const changedUserAccounts = [...userAccounts];
    changedUserAccounts.splice(index, 1);
    setUserAccounts(changedUserAccounts);
  };

  const handleAgentChange = (index: number, agentId: UUID) => {
    const accounts = [...form.getFieldValue("accounts")] as AdminCreateUpdateAgentUserAccount[];
    accounts[index] = {
      ...accounts[index],
      representingAgentId: undefined,
    } as AdminCreateUpdateAgentUserAccount;

    form.setFieldsValue({ accounts });

    const changedUserAccounts = [...userAccounts];
    changedUserAccounts[index] = {
      ...changedUserAccounts[index],
      agentId,
    } as UserAccountState;
    setUserAccounts(changedUserAccounts);
  };

  const handleRepresentingAgentChange = (index: number, agentId: UUID) => {
    const changedUserAccounts = [...userAccounts];
    changedUserAccounts[index] = {
      ...changedUserAccounts[index],
      representingAgentId: agentId,
    } as UserAccountState;
    setUserAccounts(changedUserAccounts);
  };

  const handlePermissionTreeCheck = (index: number, checked: AntTreeStrictCheckedProps) => {
    const checkedPermissions = checked.checked.map((checked) => Permission[checked as keyof typeof Permission]);
    const changedUserAccounts = [...userAccounts];
    changedUserAccounts[index] = {
      ...changedUserAccounts[index],
      permissions: filterPermissionsWithPrerequisites(permissionDefinitions, checkedPermissions),
    } as UserAccountState;
    setUserAccounts(changedUserAccounts);
  };

  const handleDefaultPermissionsSet = (permissions: Permission[], index: number) => {
    const changedUserAccounts = [...userAccounts];
    changedUserAccounts[index] = {
      ...changedUserAccounts[index],
      permissions,
    } as UserAccountState;

    setUserAccounts(changedUserAccounts);
  };

  const handleSystemAdminChange = (event: CheckboxChangeEvent) => {
    if (!event.target.checked) {
      setUserAccounts(
        [...userAccounts].map((userAccount) => ({
          ...userAccount,
          permissions: userAccount.permissions.filter(
            (permission) => !isSystemAdminOnlyPermission(permissionDefinitions, permission),
          ),
        })),
      );
    }
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const accountWithNoPermissions = userAccounts.find((account) => account.permissions.length === 0);

        if (accountWithNoPermissions) {
          message.error(
            t("user.helpers.noPermissionsForAccount", {
              aggregatedName: findAgent(accountWithNoPermissions.agentId)?.aggregatedName,
            }),
          );
        } else {
          const accounts = values.accounts.map((account, index) => ({
            ...account,
            permissions: userAccounts[index] ? (userAccounts[index] as UserAccountState).permissions : [],
          }));

          adminCreateUserWithAgentUserAccounts.mutate(
            { ...values, accounts },
            {
              onSuccess: (data) =>
                navigate(generatePath(ADMIN_USER_ROUTE_PATHS.detail.to, { id: data.id }), { replace: true }),
              onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <ContentWrapper>
      <Card
        className="card-box"
        title={
          <BackNavigationArrow>
            <h2>{t("user.titles.createUser")}</h2>
          </BackNavigationArrow>
        }
      >
        <Form form={form} layout="vertical" name="userCreateForm" onFinish={handleFormSubmit}>
          <Divider titlePlacement="start" style={{ marginTop: 0 }}>
            {t("user.titles.basicData")}
          </Divider>
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name="name"
                label={t("user.attrs.name")}
                rules={[validations.notBlank, validations.size(1, 255), validations.pattern(regexPatterns.nameRegex)]}
              >
                <Input placeholder={t("user.helpers.userNamePlaceholder")} />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name="email"
                label={t("user.attrs.email")}
                rules={[validations.notBlank, validations.size(1, 254), validations.email]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name="phone"
                label={t("user.attrs.phone")}
                rules={[validations.notBlank, validations.mobilePhoneNumber(MOBILE_NUMBER_COUNTRY_WHITELIST)]}
                normalize={phoneNumberNormalizeFunction}
              >
                <Input />
              </Form.Item>
            </Col>

            {isCurrentUserSystemAdmin && (
              <Col span={colSpan}>
                <Form.Item
                  name="systemAdmin"
                  className="form-item-without-label"
                  valuePropName="checked"
                  initialValue={false}
                  rules={[validations.none]}
                >
                  <Checkbox onChange={handleSystemAdminChange}>{t("user.attrs.systemAdmin")}</Checkbox>
                </Form.Item>
              </Col>
            )}
          </Row>
          <Divider titlePlacement="start">{t("user.titles.accounts")}</Divider>
          <Collapse
            accordion
            className="margin-top-medium"
            defaultActiveKey="0"
            items={[...Array(userAccounts.length)].map((_, index) => ({
              key: index,
              forceRender: true,
              label: findRepresentingAgent(userAccounts[index]?.representingAgentId)
                ? `${formatAgentName(findAgent(userAccounts[index]?.agentId))} | ${formatAgentName(
                    findRepresentingAgent(userAccounts[index]?.representingAgentId),
                  )}`
                : formatAgentName(findAgent(userAccounts[index]?.agentId)),
              extra: (
                <span onClick={(event) => event.stopPropagation()}>
                  <ActionButton
                    icon="minus"
                    disabled={userAccounts.length === 1}
                    onClick={() => handleAccountDelete(index)}
                  >
                    {t("user.actions.deleteAccount")}
                  </ActionButton>
                </span>
              ),
              children: (
                <>
                  <Row gutter={rowGutter}>
                    <Col span={colSpan}>
                      <Form.Item
                        name={["accounts", index, "agentId"]}
                        label={t("user.attrs.agentId")}
                        rules={[validations.notNull]}
                      >
                        <AgentSelect onChange={(agent) => handleAgentChange(index, agent)} />
                      </Form.Item>
                    </Col>

                    <Col span={colSpan}>
                      <Form.Item
                        name={["accounts", index, "representingAgentId"]}
                        label={
                          <LabelWithTooltip
                            label={t("user.attrs.representingAgentId")}
                            tooltip={t("user.helpers.representingAgentInfo")}
                          />
                        }
                        rules={[
                          findAgent(userAccounts[index]?.agentId)?.type === AgentType.LEGAL
                            ? validations.notNull
                            : validations.none,
                        ]}
                      >
                        <AgentSelect
                          optionsProps={{
                            allowedIds: findAgent(userAccounts[index]?.agentId)?.representatives?.map(
                              (representative) => representative.id,
                            ),
                            forceRender: true,
                          }}
                          disabled={findAgent(userAccounts[index]?.agentId)?.type !== AgentType.LEGAL}
                          onChange={(agent) => handleRepresentingAgentChange(index, agent)}
                          allowClear
                        />
                      </Form.Item>
                    </Col>

                    <Col span={colSpan}>
                      <Form.Item
                        name={["accounts", index, "disabled"]}
                        className="form-item-without-label"
                        valuePropName="checked"
                        initialValue={false}
                        rules={[validations.none]}
                      >
                        <Checkbox>{t("user.attrs.disabled")}</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider titlePlacement="start" className="divider-subheader">
                    {t("user.titles.permissionsSettings")}
                  </Divider>

                  <DefaultPermissionsFormPart
                    allowedFeatures={topAgentAllowedFeatures}
                    onPermissionsSet={(permissions) => handleDefaultPermissionsSet(permissions, index)}
                  />

                  <Row gutter={rowGutter}>
                    <Col span={24}>
                      <Form.Item label={t("user.attrs.permissionsLabel")} required>
                        <Tree
                          showLine={{ showLeafIcon: false }}
                          checkStrictly
                          checkable
                          checkedKeys={{ checked: userAccounts[index]?.permissions ?? [], halfChecked: [] }}
                          treeData={buildPermissionTreeNodes(
                            permissionDefinitions,
                            topAgentAllowedFeatures,
                            userAccounts[index]?.permissions,
                            isCurrentUserSystemAdmin,
                            isSystemAdmin,
                          )}
                          onCheck={(checked) => handlePermissionTreeCheck(index, checked as AntTreeStrictCheckedProps)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ),
            }))}
          />
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <ActionButton icon="plus" className="margin-top-small" onClick={handleAccountAdd}>
                {t("user.actions.addAccount")}
              </ActionButton>
            </Col>
          </Row>

          <Button className="margin-top-medium" type="primary" htmlType="submit" icon={<AntIcon type="save" />}>
            {t("common.save")}
          </Button>
        </Form>
      </Card>
    </ContentWrapper>
  );
};
